import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProfiles, addProfile, deleteProfile, setActiveProfileId, MAX_PROFILES } from '../../utils/profiles';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import './styles.css';
import { IoAddCircleOutline, IoArrowForwardCircle, IoTrashBin } from "react-icons/io5";
import { Card } from '../Card';

function getAnonymousName(index: number) {
    return `Anónimo ${index + 1}`;
}

interface Props {
    onSelect: (id: string) => void;
    navigateOnSelect?: string | null;
    renderCard?: boolean;
}

const ProfileManager: React.FC<Props> = ({ onSelect, navigateOnSelect = null, renderCard = true }) => {
    const navigate = useNavigate();
    const [profiles, setProfiles] = useState(getProfiles());
    const [limitReached, setLimitReached] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [profileToDelete, setProfileToDelete] = useState<string | null>(null);

    useEffect(() => {
        setProfiles(getProfiles());
        setLimitReached(getProfiles().length >= MAX_PROFILES);
    }, []);

    const handleCreate = () => {
        if (profiles.length >= MAX_PROFILES) {
            setLimitReached(true);
            return;
        }
        const newId = Math.random().toString(36).slice(2) + Date.now().toString(36);
        addProfile({ id: newId, name: '', data: {} });
        setProfiles(getProfiles());
        onSelect(newId);
        if (navigateOnSelect) {
            navigate(navigateOnSelect);
        }
    };

    const handleDeleteRequest = (id: string) => {
        setProfileToDelete(id);
        setConfirmOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (profileToDelete) {
            deleteProfile(profileToDelete);
            setProfiles(getProfiles());
            setProfileToDelete(null);
            setConfirmOpen(false);
        }
    };

    const handleDeleteCancel = () => {
        setProfileToDelete(null);
        setConfirmOpen(false);
    };

    const handleSelect = (id: string) => {
        setActiveProfileId(id);
        onSelect(id);
        if (navigateOnSelect) {
            navigate(navigateOnSelect);
        }
    };

    const content = (
            <div className="profile-manager">
                <h2>Crear/abrir personaje</h2>
                <ul className="profile-list">
                    {profiles.map((p, i) => (
                        <li key={p.id} className='profile-item'>
                            <button
                                className="profile-button"
                                onClick={() => handleSelect(p.id)}
                            >
                                <span className='profile-name'>{p.name && p.name.trim() !== '' ? p.name : getAnonymousName(i)}</span>
                                <IoArrowForwardCircle className='profile-button-icon' />
                            </button>
                            <button
                                className="button-remove"
                                aria-label="Eliminar"
                                onClick={() => handleDeleteRequest(p.id)}
                            >
                                <IoTrashBin />
                            </button>
                        </li>
                    ))}
                    <li className='profile-item'>
                        <button
                            className="profile-button profile-button-add"
                            onClick={handleCreate}
                            disabled={limitReached}
                        >
                            <span className='profile-name'>Nuevo personaje…</span>
                            <IoAddCircleOutline className='profile-button-icon' />
                        </button>
                    </li>
                </ul>
                {limitReached && (
                    <div className="profile-limit">
                        Has alcanzado el límite de {MAX_PROFILES} personajes.
                    </div>
                )}
                <ConfirmationModal
                    open={confirmOpen}
                    title="¿Eliminar personaje?"
                    description="Esta acción no se puede deshacer."
                    onConfirm={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                />
            </div>
    );

    if (!renderCard) {
        return content;
    }

    return <Card size='medium'>{content}</Card>;
};

export default ProfileManager;
